import React, { useState, useEffect, useCallback } from 'react';
import { templatesAPI } from '../services/api';
import { Sparkles, MessageSquare } from 'lucide-react';

const TemplateSuggester = ({ field, value, onSelect }) => {
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchSuggestions = useCallback(async (q) => {
        if (!q || q.length < 3) {
            setSuggestions([]);
            return;
        }

        setLoading(true);
        try {
            const res = await templatesAPI.list({ field, q });
            if (res.data.success) {
                // Filter out suggestions that are already exactly what's typed
                const filtered = res.data.data.filter(s => s.content.toLowerCase() !== q.toLowerCase());
                setSuggestions(filtered);
            }
        } catch (err) {
            console.error('Fetch templates error:', err);
        } finally {
            setLoading(false);
        }
    }, [field]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (value && value.length >= 3) {
                fetchSuggestions(value);
            } else {
                setSuggestions([]);
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [value, fetchSuggestions]);

    if (suggestions.length === 0) return null;

    return (
        <div className="template-suggestions-container" style={{
            position: 'relative',
            marginTop: '8px',
            zIndex: 10
        }}>
            <div style={{
                background: 'var(--surface-1)',
                border: '1px solid var(--primary-200)',
                borderRadius: 'var(--radius)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                overflow: 'hidden'
            }}>
                <div style={{
                    padding: '8px 12px',
                    background: 'var(--primary-50)',
                    borderBottom: '1px solid var(--primary-100)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '11px',
                    fontWeight: '700',
                    color: 'var(--primary-700)',
                    textTransform: 'uppercase'
                }}>
                    <Sparkles size={12} />
                    {loading ? 'Recherche...' : 'Suggestions intelligentes'}
                </div>
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {suggestions.map((s, idx) => (
                        <div
                            key={idx}
                            onClick={() => onSelect(s.content)}
                            style={{
                                padding: '10px 12px',
                                borderBottom: idx === suggestions.length - 1 ? 'none' : '1px solid var(--border)',
                                cursor: 'pointer',
                                fontSize: '13px',
                                color: 'var(--text-primary)',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '10px'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'var(--primary-50)';
                                e.currentTarget.style.color = 'var(--primary-700)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'transparent';
                                e.currentTarget.style.color = 'var(--text-primary)';
                            }}
                        >
                            <MessageSquare size={14} style={{ marginTop: '2px', flexShrink: 0, opacity: 0.5 }} />
                            <span style={{
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden'
                            }}>
                                {s.content}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default TemplateSuggester;
